import {
  Box,
  Typography,
  Icon,
  LinkButton,
  Button,
  ResponsiveLayout,
} from "@bca/ui-library";
import css from "@styled-system/css";
import React from "react";
import styled from "styled-components";
import { border, BorderProps } from "styled-system";
import { useSelector, useDispatch } from "react-redux";

import { StickyFooterProps } from "./StickyFooter";
import analytics from "../common/analytics/analytics";
import { IEventPushable } from "../common/analytics/analytics.types";
import { StoreType } from "../StoreType";
import { handlePrintExportFile } from "../utils/handlePrintExportFile";
import { GET_STATE } from "../redux/slices/appSlice";
import { useAuth } from "@bca-auth0/bca-auth0-provider";

const StyledFooter = styled(Box)<BorderProps>`
  bottom: 0;
  left: 0;
  position: fixed;
  margin-top: 15px;
  ${border}
`;

const StyledLinkButton = styled(LinkButton)`
  ${css({ borderRadius: 2, border: "normalBorder", boxShadow: "default" })}
`;

const pushPrintAnalytics = () => {
  analytics.push<IEventPushable>({
    event: {
      name: "click",
      category: "button",
      label: "print",
      action: "print",
      type: "pdf",
      section: "footer",
    },
  });
};

const pushExportAnalytics = () => {
  analytics.push<IEventPushable>({
    event: {
      name: "click",
      category: "button",
      label: "export",
      action: "export",
      type: "excel",
      section: "footer",
    },
  });
};

const pushClearAllAnalytics = () => {
  analytics.push<IEventPushable>({
    event: {
      name: "click",
      category: "button",
      label: "clear-all",
      action: "removes-all-selected-items",
      section: "footer",
    },
  });
};

const StickyFooterDesktop: React.FunctionComponent<StickyFooterProps> = ({
  selectedVehicles,
  setSelectedVehicles,
  printLink,
  exportLink,
  pdfEnabled,
}) => {
  const dispatch = useDispatch();
  const {
    app: { isLoadingFile, isToUseAuthZero },
  }: StoreType = useSelector(GET_STATE);

  const { accessToken } = useAuth(!isToUseAuthZero);

  const clearSelection = () => {
    setSelectedVehicles([]);
  };

  const getFullURL = (linkRoot: string): string => {
    const vrms = selectedVehicles.join(pdfEnabled ? "," : "|");
    return linkRoot + vrms;
  };

  return (
    <StyledFooter
      px="60px"
      backgroundColor="primary-white"
      width="100%"
      height="98px"
      display={selectedVehicles.length > 0 ? "flex" : "none"}
      css={`
        z-index: 2;
      `}
      border="normalBorder"
      data-testid="desktop-sticky-footer"
    >
      <ResponsiveLayout
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          color="footer-blue"
          m={0}
          variant="body"
          css={`
            font-weight: 500;
          `}
        >
          {selectedVehicles.length}{" "}
          {selectedVehicles.length === 1
            ? "Vehicle selected"
            : "Vehicles selected"}
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          css={`
            gap: 8px;
          `}
        >
          {/* Track button is hidden until functionality is implemented */}
          {/* <StyledLinkButton
          variant="tertiary"
          css={`
            background-color: "primary-white";
          `}
          width="89px"
          height="42px"
          px={0}
          verticalAlign="middle"
        >
          <Icon
            width="14px"
            height="14px"
            name="star"
            weight="solid"
            color="secondary-orange"
            mb="2px"
            mr={2}
          ></Icon>
          <Typography
            color="footer-blue"
            variant="body"
            css={`
              font-weight: 500;
            `}
          >
            Track
          </Typography>
        </StyledLinkButton> */}
          <StyledLinkButton
            variant="tertiary"
            css={`
              background-color: "primary-white";
              ${isLoadingFile &&
              `
                opacity: 0.3;
                cursor: default;
              `}
            `}
            width="89px"
            height="42px"
            px={0}
            href={!pdfEnabled ? getFullURL(printLink?.href) : null}
            target="_blank"
            onClick={() => {
              pdfEnabled &&
                handlePrintExportFile(
                  dispatch,
                  getFullURL(printLink.href),
                  printLink.rel,
                  accessToken,
                  isToUseAuthZero
                );
              pushPrintAnalytics();
            }}
          >
            <Icon
              width="14px"
              height="14px"
              name="print"
              weight="regular"
              color="primary-light-blue"
              mb="2px"
              mr={2}
            ></Icon>
            <Typography
              color="footer-blue"
              variant="body"
              css={`
                font-weight: 500;
              `}
            >
              Print
            </Typography>
          </StyledLinkButton>
          <StyledLinkButton
            variant="tertiary"
            css={`
              background-color: "primary-white";
              ${isLoadingFile &&
              `
                opacity: 0.3;
                cursor: default;
              `}
            `}
            width="148px"
            height="42px"
            px={0}
            href={!pdfEnabled ? getFullURL(exportLink?.href) : null}
            target="_blank"
            onClick={() => {
              pdfEnabled &&
                handlePrintExportFile(
                  dispatch,
                  getFullURL(exportLink.href),
                  exportLink.rel,
                  accessToken,
                  isToUseAuthZero
                );
              pushExportAnalytics();
            }}
          >
            <Icon
              width="14px"
              height="14px"
              name="file-export"
              weight="regular"
              color="primary-light-blue"
              mb="2px"
              mr={2}
            ></Icon>
            <Typography
              color="footer-blue"
              variant="body"
              css={`
                font-weight: 500;
              `}
            >
              Export to Excel
            </Typography>
          </StyledLinkButton>
        </Box>
        <Button
          variant="tertiary"
          onClick={() => {
            pushClearAllAnalytics();
            clearSelection();
          }}
          css={`
            display: flex;
            flex-direction: row;
          `}
          verticalAlign="middle"
          p={0}
        >
          <Icon
            width="14px"
            height="14px"
            name="trash"
            weight="regular"
            color="primary-light-blue"
            mb="2px"
            mr={2}
          ></Icon>
          <Typography
            variant="body"
            color="primary-light-blue"
            css={`
              text-decoration: underline;
            `}
          >
            Clear selected
          </Typography>
        </Button>
      </ResponsiveLayout>
    </StyledFooter>
  );
};

export default StickyFooterDesktop;
